import { useEffect, useRef, useState, type ReactNode } from 'react'
import type { TranslateFn } from './context'

/**
 * Loader IA « vivant » : un petit personnage (SVG maison) qui s'active — il
 * code, mange, fait du sport… — + des messages d'étape qui tournent toutes les
 * ~2,5 s, pour que les quelques secondes de génération ne ressemblent jamais
 * à un blocage. Purement cosmétique : les messages ne reflètent pas le vrai
 * avancement (l'API ne le donne pas).
 *
 * Les générations sont souvent plus courtes que le cycle complet : on mémorise
 * donc (au niveau du module, donc pour la durée de vie de la page) où chaque
 * phase s'est arrêtée. Le chargement suivant reprend au message et au
 * personnage suivants, au lieu de rejouer toujours les deux premiers.
 */
export type AiLoaderPhase = 'adapt' | 'letter' | 'score'

// 10 messages par phase (loader.<phase>.1 → .10 dans i18n), joués dans l'ordre :
// ils racontent une progression plausible lecture → analyse → production → finitions.
const MESSAGE_COUNT = 10
const MESSAGE_KEYS: Record<AiLoaderPhase, string[]> = {
  adapt: Array.from({ length: MESSAGE_COUNT }, (_, i) => `loader.adapt.${i + 1}`),
  letter: Array.from({ length: MESSAGE_COUNT }, (_, i) => `loader.letter.${i + 1}`),
  score: Array.from({ length: MESSAGE_COUNT }, (_, i) => `loader.score.${i + 1}`),
}

// Personnages « bâton » dessinés maison : corps en trait slate, accessoire en
// accent indigo (même langage que le reste du widget). Communs à tous les loaders.
const ACCENT = '#6366f1'
const buddy = (children: ReactNode) => (
  <svg
    viewBox="0 0 28 28"
    width="22"
    height="22"
    fill="none"
    // currentColor : le corps du personnage suit le thème (piloté en CSS via
    // la couleur du conteneur), pour rester lisible en mode sombre.
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
)

const BUDDIES: ReactNode[] = [
  // Il code (laptop sur les genoux)
  buddy(
    <>
      <circle cx="9.5" cy="6.5" r="2.4" />
      <path d="M9.5 9 V14.5" />
      <path d="M9.5 14.5 H14.5 V19.5 H17" />
      <path d="M9.5 11 L14.5 13" />
      <path d="M13.5 14 H19" stroke={ACCENT} />
      <path d="M18.7 13.8 L20.7 8.6" stroke={ACCENT} />
    </>
  ),
  // Il court
  buddy(
    <>
      <circle cx="16.5" cy="5.5" r="2.4" />
      <path d="M16 8 L13.5 14.5" />
      <path d="M15.5 9.5 L19.5 12" />
      <path d="M15.5 9.5 L11.5 11" />
      <path d="M13.5 14.5 L18 17.5 L17.5 22" />
      <path d="M13.5 14.5 L9.5 16 L6.5 13.5" />
      <path d="M4 7.5 H8" stroke={ACCENT} />
      <path d="M3 11.5 H6.5" stroke={ACCENT} />
    </>
  ),
  // Pause café
  buddy(
    <>
      <circle cx="11.5" cy="6.5" r="2.4" />
      <path d="M11.5 9 V16.5" />
      <path d="M11.5 16.5 L9 22" />
      <path d="M11.5 16.5 L14 22" />
      <path d="M11.5 11.5 L16.5 12.5" />
      <path d="M16.5 10.5 h4 v2.5 a2 2 0 0 1 -2 2 a2 2 0 0 1 -2 -2 Z" stroke={ACCENT} />
      <path d="M17.8 8.5 q0.6 -1 0 -2" stroke={ACCENT} />
      <path d="M19.6 8.5 q0.6 -1 0 -2" stroke={ACCENT} />
    </>
  ),
  // Haltérophile
  buddy(
    <>
      <path d="M6.5 6.5 H21.5" stroke={ACCENT} />
      <path d="M8 4 V9" stroke={ACCENT} strokeWidth="2.6" />
      <path d="M20 4 V9" stroke={ACCENT} strokeWidth="2.6" />
      <circle cx="14" cy="9.5" r="2.2" />
      <path d="M14 11.7 V17" />
      <path d="M14 12.5 L10 6.8" />
      <path d="M14 12.5 L18 6.8" />
      <path d="M14 17 L10.5 22" />
      <path d="M14 17 L17.5 22" />
    </>
  ),
  // Il lit
  buddy(
    <>
      <circle cx="14" cy="5.5" r="2.4" />
      <path d="M14 8 V13.5" />
      <path d="M14 10 L9.5 15" />
      <path d="M14 10 L18.5 15" />
      <path
        d="M7.5 16.5 Q10.75 14.8 14 16.5 Q17.25 14.8 20.5 16.5 V20.5 Q17.25 18.8 14 20.5 Q10.75 18.8 7.5 20.5 Z"
        stroke={ACCENT}
      />
      <path d="M14 16.5 V20.5" stroke={ACCENT} />
    </>
  ),
  // Part de pizza
  buddy(
    <>
      <circle cx="11.5" cy="6.5" r="2.4" />
      <path d="M11.5 9 V16.5" />
      <path d="M11.5 16.5 L9 22" />
      <path d="M11.5 16.5 L14 22" />
      <path d="M11.5 11.5 L16 9.5" />
      <path d="M16.5 6.5 L22 8 L18 11.5 Z" stroke={ACCENT} />
      <circle cx="19.2" cy="8.4" r="0.5" fill={ACCENT} stroke="none" />
    </>
  ),
  // Yoga (posture de l'arbre)
  buddy(
    <>
      <circle cx="14" cy="5.8" r="2.4" />
      <path d="M14 8.2 V16" />
      <path d="M14 10.5 L9.5 6" />
      <path d="M14 10.5 L18.5 6" />
      <path d="M14 16 V22.5" />
      <path d="M14 16 L17.5 17.5 L14.5 19" />
      <path d="M5.5 10 v2 M4.5 11 h2" stroke={ACCENT} strokeWidth="1.6" />
      <path d="M22.5 12 v2 M21.5 13 h2" stroke={ACCENT} strokeWidth="1.6" />
    </>
  ),
  // Eurêka (ampoule)
  buddy(
    <>
      <circle cx="14" cy="3.8" r="1.9" stroke={ACCENT} />
      <path d="M11.5 2 L10.4 1" stroke={ACCENT} strokeWidth="1.6" />
      <path d="M16.5 2 L17.6 1" stroke={ACCENT} strokeWidth="1.6" />
      <circle cx="14" cy="9.8" r="2.4" />
      <path d="M14 12.2 V18.5" />
      <path d="M14 13.8 L10 15.5" />
      <path d="M14 13.8 L17.6 12.3 L16.8 9.3" />
      <path d="M14 18.5 L11 23" />
      <path d="M14 18.5 L17 23" />
    </>
  ),
]

// Points de reprise entre deux chargements (mémoire du module, pas de storage).
const nextMsgStart: Record<AiLoaderPhase, number> = { adapt: 0, letter: 0, score: 0 }
let nextBuddyStart = Math.floor(Math.random() * BUDDIES.length)

export default function AiLoader({
  phase,
  t,
  overlay = false,
  variant = 'default',
}: {
  phase: AiLoaderPhase
  /** Traduction fournie par l'app (voir context.ts). */
  t: TranslateFn
  overlay?: boolean
  /** 'ring' : le personnage tourne au centre d'un anneau indéterminé — écho de
   *  l'anneau de score final (utilisé pendant le calcul de compatibilité). */
  variant?: 'default' | 'ring'
}) {
  const keys = MESSAGE_KEYS[phase]
  const [step, setStep] = useState(0)
  // Points de départ figés au montage ; les compteurs module avancent ensuite.
  const msgStart = useRef(nextMsgStart[phase]).current
  const buddyStart = useRef(nextBuddyStart).current
  useEffect(() => {
    const id = setInterval(() => setStep((s) => s + 1), 2500)
    return () => clearInterval(id)
  }, [])
  const msgIdx = (msgStart + step) % keys.length
  const buddyIdx = (buddyStart + step) % BUDDIES.length
  // Après chaque étape affichée, note où le prochain chargement devra reprendre.
  useEffect(() => {
    nextMsgStart[phase] = (msgIdx + 1) % keys.length
    nextBuddyStart = (buddyIdx + 1) % BUDDIES.length
  })
  const isRing = variant === 'ring'
  const body = (
    <div
      className={`ai-loader${isRing ? ' ai-loader--ring' : ''}`}
      role="status"
      aria-live="polite"
      data-testid="ai-loader"
    >
      {isRing ? (
        // Anneau indéterminé qui tourne, personnage au centre : pendant le calcul
        // de compatibilité, il annonce l'anneau de score qui va se remplir.
        <span className="ai-loader-ring" aria-hidden="true">
          <svg className="ai-loader-ring-svg" viewBox="0 0 44 44" width="44" height="44">
            <circle className="ai-loader-ring-track" cx="22" cy="22" r="19" fill="none" strokeWidth="3.2" />
            <circle className="ai-loader-ring-arc" cx="22" cy="22" r="19" fill="none" strokeWidth="3.2" strokeLinecap="round" />
          </svg>
          {/* key force le remount à chaque étape -> rejoue le pop du personnage. */}
          <span className="ai-loader-ring-buddy" key={`b${step}`}>
            {BUDDIES[buddyIdx]}
          </span>
        </span>
      ) : (
        // key force le remount à chaque étape -> rejoue le pop d'apparition.
        <span className="ai-loader-buddy" key={`b${step}`} aria-hidden="true">
          {BUDDIES[buddyIdx]}
        </span>
      )}
      <span className="ai-loader-msg" key={`m${step}`}>
        {t(keys[msgIdx])}
      </span>
    </div>
  )
  // En overlay (preview), le loader flotte au-dessus du document estompé.
  return overlay ? <div className="ai-loader-overlay" data-testid="ai-loader-overlay">{body}</div> : body
}
